import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Button, TextField, Switch, FormControlLabel, Divider, IconButton,
  Snackbar, Tooltip,
} from "@mui/material";
import SmartToyIcon        from "@mui/icons-material/SmartToy";
import RefreshIcon         from "@mui/icons-material/Refresh";
import PersonAddIcon       from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon    from "@mui/icons-material/PersonRemove";
import WarningAmberIcon    from "@mui/icons-material/WarningAmber";
import api from "../../utils/api";

const fmtDate = (d) => d ? new Date(d).toLocaleString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "N/A";
const fmtNum  = (n) => n == null ? "N/A" : Number(n).toLocaleString();

export default function AIChatSettings() {
  const [config,      setConfig]      = useState(null);
  const [logs,        setLogs]        = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [snack,       setSnack]       = useState("");
  const [newUser,     setNewUser]     = useState("");
  const [allUsers,    setAllUsers]    = useState([]);  // full user list for autocomplete
  const [userSearch,  setUserSearch]  = useState([]);  // filtered suggestions
  const [userErr,     setUserErr]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, logsRes] = await Promise.all([
        api.get("/ai-chat/config"),
        api.get("/ai-chat/logs?limit=100"),
      ]);
      const cfg = cfgRes.data || {};
      const safeJsonArray = (v) => { try { const a = JSON.parse(v || "[]"); return Array.isArray(a) ? a : []; } catch { return []; } };
      setConfig({
        enabled:                  cfg.enabled === true || cfg.enabled === 1,
        allowed_usernames:        safeJsonArray(cfg.allowed_usernames),
        allowed_roles:            safeJsonArray(cfg.allowed_roles),
        max_tokens:               cfg.max_tokens               || 5000,
        rate_limit_per_hour:      cfg.rate_limit_per_hour      || 20,
        monthly_token_budget:     cfg.monthly_token_budget     || "",
        proactive_alerts_enabled: cfg.proactive_alerts_enabled === true || cfg.proactive_alerts_enabled === 1,
        alert_email:              cfg.alert_email              || "",
      });
      const logData = logsRes.data || [];
      setLogs(logData);

      // Compute stats
      const totalConversations = logData.length;
      const totalTokensIn  = logData.reduce((s, l) => s + (l.tokens_in  || 0), 0);
      const totalTokensOut = logData.reduce((s, l) => s + (l.tokens_out || 0), 0);
      const errors = logData.filter(l => l.error).length;
      const emailed = logData.filter(l => l.emailed_to).length;
      const uniqueUsers = new Set(logData.map(l => l.username)).size;

      // Most queried tools
      const toolCounts = {};
      logData.forEach(l => {
        if (l.tools_called) {
          try {
            JSON.parse(l.tools_called).forEach(t => {
              const name = t.tool || t;
              toolCounts[name] = (toolCounts[name] || 0) + 1;
            });
          } catch {}
        }
      });

      setStats({ totalConversations, totalTokensIn, totalTokensOut,
        totalTokens: totalTokensIn + totalTokensOut,
        errors, emailed, uniqueUsers, toolCounts });
    } catch (e) {
      setSnack("Failed to load AI Chat settings");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai-chat/config", {
        enabled:                  config.enabled,
        allowed_usernames:        config.allowed_usernames,
        allowed_roles:            config.allowed_roles,
        max_tokens:               Math.min(config.max_tokens, 5000),
        rate_limit_per_hour:      config.rate_limit_per_hour,
        monthly_token_budget:     config.monthly_token_budget || null,
        proactive_alerts_enabled: config.proactive_alerts_enabled,
        alert_email:              config.alert_email || null,
      });
      setSnack("AI Chat settings saved");
    } catch { setSnack("Failed to save settings"); }
    finally { setSaving(false); }
  };

  // Load all users once for autocomplete
  useEffect(() => {
    api.get("/users").then(r => setAllUsers(r.data || [])).catch(() => {});
  }, []);

  // Filter users as admin types
  const handleUserInput = (val) => {
    setNewUser(val);
    setUserErr("");
    if (val.trim().length < 2) { setUserSearch([]); return; }
    const q = val.trim().toLowerCase();
    const matches = allUsers.filter(u => {
      const uname = (u.Username || u.username || "").toLowerCase();
      const name  = `${u.Name || ""} ${u.Lastname || ""}`.toLowerCase();
      return uname.includes(q) || name.includes(q);
    }).slice(0, 8);
    setUserSearch(matches);
  };

  const addUser = (usernameOverride) => {
    const u = (usernameOverride || newUser).trim().toLowerCase();
    if (!u) return;
    if (config.allowed_usernames.includes(u)) {
      setUserErr(`"${u}" is already in the list.`);
      return;
    }
    // Validate against the real user list
    const realUser = allUsers.find(x => (x.Username || x.username || "").toLowerCase() === u);
    if (!realUser) {
      setUserErr(`"${u}" does not match any system user. Select from the suggestions.`);
      return;
    }
    setConfig(c => ({ ...c, allowed_usernames: [...c.allowed_usernames, u] }));
    setNewUser("");
    setUserSearch([]);
    setUserErr("");
  };

  const removeUser = (u) => setConfig(c => ({ ...c, allowed_usernames: c.allowed_usernames.filter(x => x !== u) }));

  if (loading) return <Box sx={{ py:6, textAlign:"center" }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:3 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
          <SmartToyIcon sx={{ color:"#2f63ad", fontSize:28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>AI Chat Configuration</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c" }}>Manage access, token limits, and monitor usage</Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load} size="small"><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* ── Usage stats ── */}
      {stats && (
        <Grid container spacing={2} sx={{ mb:3 }}>
          {[
            { label:"Total Conversations", value:fmtNum(stats.totalConversations), color:"#2f63ad", bg:"rgba(47,99,173,0.08)" },
            { label:"Total Tokens Used",   value:fmtNum(stats.totalTokens),        color:"#0f766e", bg:"rgba(15,118,110,0.08)" },
            { label:"Tokens In",           value:fmtNum(stats.totalTokensIn),      color:"#6b778c", bg:"rgba(107,119,140,0.08)" },
            { label:"Tokens Out",          value:fmtNum(stats.totalTokensOut),     color:"#7c3aed", bg:"rgba(124,58,237,0.08)" },
            { label:"Unique Users",        value:fmtNum(stats.uniqueUsers),        color:"#0891b2", bg:"rgba(8,145,178,0.08)" },
            { label:"Emails Sent",         value:fmtNum(stats.emailed),            color:"#15803d", bg:"rgba(21,128,61,0.08)" },
            { label:"Errors",              value:fmtNum(stats.errors),             color: stats.errors > 0 ? "#dc2626" : "#16a34a", bg: stats.errors > 0 ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)" },
          ].map(s => (
            <Grid item xs={6} sm={4} md={3} key={s.label}>
              <Paper elevation={0} sx={{ p:2, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", backgroundColor:"#fff" }}>
                <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem" }}>{s.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight:800, color:"#223142", lineHeight:1.2, mt:0.25 }}>{s.value}</Typography>
              </Paper>
            </Grid>
          ))}
          {/* Token limit gauge */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper elevation={0} sx={{ p:2, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)" }}>
              <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem" }}>Token Limit Per Response</Typography>
              <Box sx={{ display:"flex", alignItems:"baseline", gap:1, mt:0.25 }}>
                <Typography variant="h5" sx={{ fontWeight:800, color:"#2f63ad" }}>{fmtNum(config?.max_tokens)}</Typography>
                <Typography variant="caption" sx={{ color:"#9aa5b4" }}>/ 5,000 max</Typography>
              </Box>
              <Box sx={{ mt:1, height:6, borderRadius:3, backgroundColor:"rgba(47,99,173,0.1)", overflow:"hidden" }}>
                <Box sx={{ height:"100%", borderRadius:3, width:`${((config?.max_tokens||0)/5000)*100}%`, backgroundColor:"#2f63ad" }} />
              </Box>
            </Paper>
          </Grid>

          {/* Most used tools */}
          {Object.keys(stats.toolCounts).length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p:2, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)" }}>
                <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem", mb:1, display:"block" }}>Most Queried Modules</Typography>
                <Box sx={{ display:"flex", gap:1, flexWrap:"wrap" }}>
                  {Object.entries(stats.toolCounts).sort((a,b)=>b[1]-a[1]).map(([tool, count]) => (
                    <Chip key={tool} label={`${tool.replace("query_","")} (${count})`} size="small"
                      sx={{ backgroundColor:"rgba(47,99,173,0.08)", color:"#2f63ad", fontWeight:600, fontSize:"0.7rem" }} />
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      <Divider sx={{ mb:3 }} />

      {/* ── Configuration ── */}
      {config && (
        <Grid container spacing={3} sx={{ mb:3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p:2.5, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Access Control</Typography>

              <FormControlLabel
                control={<Switch checked={config.enabled} onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} color="primary" />}
                label={<Box><Typography variant="body2" sx={{ fontWeight:600 }}>AI Chat Enabled</Typography><Typography variant="caption" sx={{ color:"#6b778c" }}>When off, no users can access the chat</Typography></Box>}
                sx={{ alignItems:"flex-start", gap:1, mb:2 }}
              />

              <Typography variant="body2" sx={{ fontWeight:600, mb:1 }}>Allowed Users</Typography>
              <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.75, mb:1.5, minHeight:32 }}>
                {config.allowed_usernames.length === 0
                  ? <Typography variant="caption" sx={{ color:"#9aa5b4" }}>No users explicitly listed (use role assignment instead)</Typography>
                  : config.allowed_usernames.map(u => (
                    <Chip key={u} label={u} size="small" onDelete={() => removeUser(u)}
                      deleteIcon={<PersonRemoveIcon sx={{ fontSize:"14px !important" }} />}
                      sx={{ backgroundColor:"rgba(47,99,173,0.10)", color:"#1d4ed8", fontWeight:600, fontSize:"0.7rem" }} />
                  ))
                }
              </Box>
              <Box sx={{ position:"relative" }}>
                <Box sx={{ display:"flex", gap:1 }}>
                  <TextField size="small" placeholder="Search username…" value={newUser}
                    onChange={e => handleUserInput(e.target.value)}
                    onKeyDown={e => e.key==="Enter" && addUser()}
                    sx={{ flex:1, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}
                    InputProps={{ endAdornment: null }}
                  />
                  <Button variant="outlined" size="small" onClick={() => addUser()} startIcon={<PersonAddIcon />}
                    sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Add</Button>
                </Box>
                {/* Autocomplete dropdown */}
                {userSearch.length > 0 && (
                  <Paper elevation={4} sx={{ position:"absolute", zIndex:10, top:"100%", left:0, right:48, borderRadius:"10px", overflow:"hidden", mt:0.5, border:"1px solid rgba(47,99,173,0.15)" }}>
                    {userSearch.map(u => {
                      const uname = u.Username || u.username || "";
                      const label = [u.FirstName || u.first_name, u.LastName || u.last_name].filter(Boolean).join(" ");
                      return (
                        <Box key={uname} onClick={() => addUser(uname)}
                          sx={{ px:2, py:1, cursor:"pointer", "&:hover":{ bgcolor:"rgba(47,99,173,0.06)" }, display:"flex", alignItems:"center", gap:1.5 }}>
                          <Box sx={{ width:28, height:28, borderRadius:"50%", bgcolor:"rgba(47,99,173,0.12)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                            <Typography variant="caption" sx={{ fontWeight:700, color:"#2f63ad" }}>{uname[0]?.toUpperCase()}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>{uname}</Typography>
                            {label && <Typography variant="caption" sx={{ color:"#6b778c" }}>{label}</Typography>}
                          </Box>
                        </Box>
                      );
                    })}
                  </Paper>
                )}
                {userErr && <Typography variant="caption" sx={{ color:"#dc2626", mt:0.5, display:"block" }}>{userErr}</Typography>}
              </Box>

              <Alert severity="info" sx={{ mt:2, borderRadius:"10px", fontSize:"0.75rem" }}>
                Users with the <strong>use_ai_chat</strong> permission via their role also get access automatically.
                System Admin always has full access.
              </Alert>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p:2.5, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Token Limit</Typography>
              <Typography variant="body2" sx={{ color:"#6b778c", mb:1.5 }}>
                Maximum tokens per AI response. Hard ceiling is 5,000. Lower values reduce cost and keep responses concise.
              </Typography>
              <TextField fullWidth size="small" type="number" label="Max tokens per response"
                value={config.max_tokens}
                onChange={e => setConfig(c => ({ ...c, max_tokens: Math.min(parseInt(e.target.value)||1000, 5000) }))}
                inputProps={{ min:500, max:5000, step:500 }}
                sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}
                helperText={`${config.max_tokens.toLocaleString()} tokens · Recommended: 3,000-5,000 for detailed reports`} />

              <Box sx={{ mt:2.5 }}>
                <Typography variant="body2" sx={{ fontWeight:600, mb:1 }}>Anthropic API Key Status</Typography>
                {process.env.ANTHROPIC_API_KEY === "sk-ant-your-key-here" || !process.env.ANTHROPIC_API_KEY
                  ? <Alert severity="warning" sx={{ borderRadius:"10px" }}>
                      <strong>API key not configured.</strong> Contact your system administrator to configure the Anthropic API key.
                    </Alert>
                  : <Alert severity="success" sx={{ borderRadius:"10px" }}>API key is configured.</Alert>
                }
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display:"flex", justifyContent:"flex-end" }}>
              <Button variant="contained" onClick={save} disabled={saving}
                sx={{ background:"#2f63ad", "&:hover":{ background:"#1e4c8a" }, borderRadius:"10px", textTransform:"none", fontWeight:700, px:3 }}>
                {saving ? <CircularProgress size={18} sx={{ color:"#fff" }} /> : "Save Settings"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      <Divider sx={{ mb:3 }} />

      {/* ── Audit log ── */}
      <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>Recent Activity (last 100)</Typography>
      {logs.length === 0
        ? <Alert severity="info" sx={{ borderRadius:"12px" }}>No chat activity recorded yet.</Alert>
        : (
          <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
            <TableContainer sx={{ maxHeight:400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Time","User","Question","Modules","Tokens","Email","IP"].map(h => (
                      <TableCell key={h} sx={{ fontWeight:700, fontSize:"0.7rem", backgroundColor:"#f8fafc", color:"#5f6b7a", py:0.875 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id} hover sx={{ backgroundColor: log.error ? "rgba(220,38,38,0.02)" : undefined }}>
                      <TableCell sx={{ fontSize:"0.72rem", whiteSpace:"nowrap", color:"#5f6b7a" }}>{fmtDate(log.created_at)}</TableCell>
                      <TableCell sx={{ fontSize:"0.75rem", fontWeight:600, color:"#223142" }}>{log.username}</TableCell>
                      <TableCell sx={{ fontSize:"0.72rem", maxWidth:200 }}>
                        {log.error
                          ? <Box sx={{ display:"flex", gap:0.5, alignItems:"center" }}><WarningAmberIcon sx={{ fontSize:12, color:"#dc2626" }} /><Typography variant="caption" sx={{ color:"#dc2626" }}>{log.error.substring(0,60)}</Typography></Box>
                          : <Typography variant="caption" sx={{ color:"#374151" }}>{(log.question||"").substring(0,80)}{(log.question||"").length>80?"…":""}</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display:"flex", gap:0.5, flexWrap:"wrap" }}>
                          {log.tools_called && (() => {
                            try { return JSON.parse(log.tools_called).map(t => (
                              <Chip key={t.tool} label={(t.tool||"").replace("query_","")} size="small" sx={{ height:16, fontSize:"0.58rem", backgroundColor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} />
                            )); } catch { return null; }
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize:"0.72rem", color:"#5f6b7a", whiteSpace:"nowrap" }}>
                        {log.tokens_in||log.tokens_out ? `${((log.tokens_in||0)+(log.tokens_out||0)).toLocaleString()}` : "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontSize:"0.7rem", color:"#15803d" }}>{log.emailed_to || "N/A"}</TableCell>
                      <TableCell sx={{ fontSize:"0.68rem", color:"#9aa5b4", fontFamily:"monospace" }}>{log.ip_address || "N/A"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      }

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack("")} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.includes("Failed") ? "error" : "success"} onClose={() => setSnack("")} sx={{ borderRadius:"10px" }}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
